import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import PortalLayout from '../components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, Unlock, CheckCircle, AlertTriangle,
  Eye, Send, Calendar, MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccessPassPurchase = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hasActivePass, setHasActivePass] = useState(false);
  const [abnVerified, setAbnVerified] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/talent/access-status`);
      setHasActivePass(response.data.has_active_pass);
      setAbnVerified(response.data.abn_verified);
    } catch (error) {
      console.error('Failed to check status');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!abnVerified) {
      toast.error('ABN verification required to purchase access pass');
      navigate('/portal/settings');
      return;
    }

    setProcessing(true);
    
    try {
      const response = await axios.post(`${API}/portal/talent/purchase-access`);
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
      
    } catch (error) {
      toast.error('Failed to start checkout');
      setProcessing(false);
    }
  };

  const passPrice = 199; // $199 for 30 days

  return (
    <PortalLayout>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">GoTalent Access Pass</h1>
          <p className="text-muted-foreground">Unlock premium features to connect with candidates</p>
        </div>

        {hasActivePass ? (
          <Card className="border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
              <h2 className="text-2xl font-bold text-foreground mb-2">Access Pass Active</h2>
              <p className="text-muted-foreground mb-6">
                You have full access to GoTalent features
              </p>
              <button
                onClick={() => navigate('/portal/talent')}
                className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700"
              >
                Browse Candidates
              </button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* ABN Requirement */}
            {!abnVerified && (
              <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800 mb-6">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-yellow-800 dark:text-yellow-200 mb-1">
                        ABN Verification Required
                      </p>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        Your company ABN must be verified before purchasing a GoTalent Access Pass.
                        This ensures a professional and trustworthy marketplace.
                      </p>
                      <button
                        onClick={() => navigate('/portal/settings')}
                        className="mt-3 text-sm font-semibold text-yellow-800 hover:text-yellow-900 underline"
                      >
                        Verify ABN in Settings →
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pricing Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>30-Day Access Pass</CardTitle>
                  <div className="text-3xl font-bold text-green-600">${passPrice}</div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-muted-foreground">
                  Get full access to GoTalent candidate marketplace for 30 days
                </p>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-foreground">View Contact Details</div>
                      <p className="text-sm text-muted-foreground">Access email and phone for all discoverable candidates</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-foreground">Send Job Offers</div>
                      <p className="text-sm text-muted-foreground">Send personalized job offers directly to candidates</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-foreground">Invite to Interviews</div>
                      <p className="text-sm text-muted-foreground">Send interview invitations with proposed times</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-foreground">Unlimited Searches</div>
                      <p className="text-sm text-muted-foreground">Search and view as many candidates as you need</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-foreground">In-App Messaging</div>
                      <p className="text-sm text-muted-foreground">Communicate with candidates securely</p>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-border">
                  <button
                    onClick={handlePurchase}
                    disabled={!abnVerified || processing}
                    className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing ? 'Processing...' : abnVerified ? 'Purchase Access Pass' : 'ABN Verification Required'}
                  </button>
                  
                  <p className="text-xs text-center text-muted-foreground mt-3">
                    Secure payment powered by Stripe. Cancel anytime.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Free Features */}
            <Card>
              <CardHeader>
                <CardTitle>Free Features (Always Available)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-blue-600" />
                  <span>Browse candidate profiles</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-600" />
                  <span>Send in-app messages</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                  <span>View candidate skills & experience</span>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PortalLayout>
  );
};

export default AccessPassPurchase;
